import {
  always,
  canBuildShop,
  hasShop,
  hasShopAccess,
  isAdmin,
  isAdminAtShop,
  isAffiliate,
  isNotTcShop,
  isSuperadmin,
  runsEvents,
  seesReports,
  seesShopReporting,
  sellsDirectly,
} from "./guards";
import { SECTION } from "./sections";

const SIDEBAR = ["dashboard", "account-sidebar", "mobile"];
const ACCOUNT_MENU = ["account-menu"];

const page = (id, load, { title = null, guard, nav, ...rest }) => ({
  id,
  load,
  title,
  guard,
  ...(nav ? { nav } : {}),
  ...rest,
});

const shopNav = (label, iconName, extra = {}) => ({
  label,
  iconName,
  section: SECTION.SHOP_DASHBOARD,
  contexts: SIDEBAR,
  ...extra,
});

const accountNav = (label, iconName) => ({
  label,
  iconName,
  section: SECTION.USER_ACCOUNT,
  contexts: ACCOUNT_MENU,
  exact: true,
});

const adminNav = (section) => (label, iconName) => ({
  label,
  iconName,
  section,
  contexts: SIDEBAR,
});

const ordersNav = adminNav(SECTION.ADMIN_ORDERS);
const contentNav = adminNav(SECTION.ADMIN_CONTENT);
const usersNav = adminNav(SECTION.ADMIN_USERS);
const marketingNav = adminNav(SECTION.ADMIN_MARKETING);
const systemNav = adminNav(SECTION.ADMIN_SYSTEM);

const shopHomeLabel = ({ activeInfluencer: shop }) =>
  shop.isAgentShop ? "Agent Overview" : "My Dashboard";

const SHOP = [
  page("shop.home", () => import("../pages/Dashboard/ShopHome"), {
    guard: hasShop,
    showFloatingActions: true,
    nav: shopNav(shopHomeLabel, "MyDashboardIcon", { exact: true }),
  }),
  page("shop.builder", () => import("../pages/Dashboard/ShopEditor"), {
    title: "Shop Builder",
    guard: canBuildShop,
    showFloatingActions: true,
    nav: shopNav("Shop Builder", "ShopBuilderIcon"),
  }),
  page("shop.reporting", () => import("../pages/Dashboard/ShopReporting"), {
    guard: seesShopReporting,
    showFloatingActions: true,
    nav: shopNav("Reporting", "ReportingIcon"),
  }),
  page("shop.sales", () => import("../pages/Dashboard/SalesOverview"), {
    title: "Sales Overview",
    guard: sellsDirectly,
    showFloatingActions: true,
    nav: shopNav("Sales Overview", "SalesIcon", {
      exact: true,
      badge: "unsignedCount",
    }),
  }),
  page(
    "shop.events",
    () => import("../components/TalentDashboard/ManageEvents"),
    {
      title: "Manage Events",
      guard: runsEvents,
      nav: shopNav("Manage Events", "ManageEventsIcon", { exact: true }),
    },
  ),
  page("shop.event-new", () => import("../pages/Dashboard/EventForm"), {
    title: "Create Event",
    guard: runsEvents,
  }),
  page("shop.event", () => import("../pages/Dashboard/EventView"), {
    title: "Past Event",
    guard: runsEvents,
  }),
  page("shop.event-edit", () => import("../pages/Dashboard/EventForm"), {
    title: "Edit Event",
    guard: runsEvents,
  }),
  page("shop.return-label", () => import("../pages/Dashboard/ReturnLabel"), {
    title: "Return Label",
    guard: runsEvents,
    showFloatingActions: true,
    nav: shopNav("Return Label", "ReturnKitsIcon", { exact: true }),
  }),
  page("shop.payouts", () => import("../pages/GetPaidV2"), {
    title: "Payout",
    guard: isNotTcShop,
    nav: shopNav("Get Paid", "GetPaidIcon"),
  }),
  page("dashboard.referral", () => import("../pages/ReferralProgram"), {
    title: "Referral Program",
    guard: hasShopAccess,
    nav: shopNav("Make More $", "ReferralIcon"),
  }),
  page(
    "shop.settings",
    () => import("../components/TalentDashboard/AccountSettings"),
    {
      title: "Account Settings",
      guard: isNotTcShop,
      nav: shopNav("Account Settings", "AccountSettingsIcon"),
    },
  ),
  page("shop.address", () => import("../pages/AddressConfirmation"), {
    title: "Address Confirmation",
    guard: isNotTcShop,
  }),
  page("shop.analytics", () => import("../pages/AdminShopAnalytics"), {
    title: "Admin Analytics",
    guard: isAdminAtShop,
    nav: shopNav("Admin Analytics", "AnalyticsIcon", { exact: true }),
  }),
  page("shop.print-box", () => import("../pages/Dashboard/PrintBoxSigning"), {
    title: "Print Box Overview",
    guard: hasShop,
    showFloatingActions: true,
  }),
];

const DASHBOARD = [
  page("dashboard.start", () => import("../pages/Dashboard/StartingShop"), {
    guard: always,
  }),
];

const ACCOUNT = [
  page("account.affiliate", () => import("../components/AffiliateDashboard"), {
    title: "Affiliate Portal",
    guard: isAffiliate,
    nav: accountNav("Affiliate Portal", "DashboardRounded"),
  }),
  page(
    "account.orders",
    () => import("../components/AccountDashboard/Orders"),
    {
      title: "My Orders",
      guard: always,
      nav: accountNav("My Orders", "ListOutlined"),
    },
  ),
  page("account.order", () => import("../pages/AccountDashboard/Order"), {
    title: "Order",
    guard: always,
  }),
  page("account.wishlist", () => import("../pages/Wishlist"), {
    guard: always,
    nav: accountNav("Wishlist", "StarsIcon"),
  }),
  page("account.favorites", () => import("../pages/MyFavourites"), {
    title: "My Favorites",
    guard: always,
    nav: accountNav("My Favorites", "FavoriteBorder"),
  }),
  page("account.notifications", () => import("../pages/Notifications"), {
    title: "Notifications",
    guard: always,
    nav: accountNav("Notifications", "NotificationsOutlined"),
  }),
  page("account.security", () => import("../components/AccountSecurity"), {
    title: "Sign-in & Security",
    guard: always,
    nav: accountNav("Sign-in & Security", "VerifiedUserOutlined"),
  }),
  page("account.no-shop", () => import("../pages/Whoops"), {
    title: "Whoops!",
    guard: always,
  }),
];

const ADMIN = [
  page("admin.index", () => import("../pages/AdminIndex"), {
    title: "Admin",
    guard: isAdmin,
    nav: { ...ordersNav("Admin Home", "DashboardRounded"), exact: true },
  }),
  page("admin.orders", () => import("../pages/AdminSearchOrders"), {
    title: "Search Orders",
    guard: isAdmin,
    nav: ordersNav("Search Orders", "SearchOutlined"),
  }),
  page("admin.shipping-assistant", () => import("../pages/ShippingAssistant"), {
    title: "Shipping Assistant",
    guard: isAdmin,
    nav: ordersNav("Shipping Assistant", "CheckmarkIcon"),
  }),
  page("admin.print-boxes", () => import("../pages/PrintBoxes"), {
    title: "Print Boxes",
    guard: isAdmin,
    nav: ordersNav("Print Boxes", "LocalShipping"),
  }),
  page("admin.print-box-new", () => import("../pages/PrintBox"), {
    guard: isAdmin,
  }),
  page("admin.print-box", () => import("../pages/PrintBox"), {
    guard: isAdmin,
  }),
  page("admin.payouts", () => import("../pages/AdminPayouts"), {
    title: "Payouts",
    guard: isSuperadmin,
    nav: ordersNav("Payouts", "AccountBalanceWalletOutlined"),
  }),
  page(
    "admin.product-approval",
    () => import("../pages/AdminPresignedApproval"),
    {
      title: "Product Approval",
      guard: isAdmin,
      nav: contentNav("Product Approval", "AssignmentTurnedInOutlined"),
    },
  ),
  page("admin.products", () => import("../pages/AdminProducts"), {
    title: "Product Search",
    guard: isAdmin,
    nav: contentNav("Product Search", "FindInPageOutlined"),
  }),
  page("admin.image-approval", () => import("../pages/AdminImageApproval"), {
    title: "Image Approval",
    guard: isAdmin,
    nav: contentNav("Image Approval", "FindInPageOutlined"),
  }),
  page("admin.product-types", () => import("../pages/AdminProductTypes"), {
    title: "Product Type Assignment",
    guard: isAdmin,
    nav: contentNav("Product Type Assignment", "AssignmentTurnedInOutlined"),
  }),
  page("admin.categories", () => import("../pages/AdminCategories"), {
    title: "Manage Categories",
    guard: isAdmin,
    nav: contentNav("Categories", "CategoryIcon"),
  }),
  page("admin.franchises", () => import("../pages/AdminFranchises"), {
    title: "Manage Franchises",
    guard: isAdmin,
    nav: contentNav("Franchises", "TheatersIcon"),
  }),
  page("admin.franchise-search", () => import("../pages/FranchiseSearch"), {
    title: "Franchise Search",
    guard: isAdmin,
  }),
  page("admin.wishlisted-products", () => import("../pages/Wishlists"), {
    title: "Wishlisted Products",
    guard: isAdmin,
    nav: contentNav("Wishlisted Products", "StarsIcon"),
  }),
  page(
    "admin.account-approval",
    () => import("../pages/AdminAccountApproval"),
    {
      title: "Account Approval",
      guard: isAdmin,
      nav: usersNav("Account Approval", "AccountBoxIcon"),
    },
  ),
  page("admin.users", () => import("../pages/AdminUsers"), {
    title: "Users Search",
    guard: isAdmin,
    nav: usersNav("Users Search", "EmojiPeople"),
  }),
  page("admin.user-actions", () => import("../pages/AdminUserActions"), {
    title: "User Actions",
    guard: isAdmin,
    nav: usersNav("User Actions", "FormatListNumberedRtl"),
  }),
  page("admin.access-links", () => import("../pages/AdminAccessLinks"), {
    title: "Access Links",
    guard: isAdmin,
    nav: usersNav("Access Links", "LinkOutlined"),
  }),
  page("admin.event-calendar", () => import("../pages/EventCalendar"), {
    title: "Event Calendar",
    guard: isAdmin,
    nav: marketingNav("Event Calendar", "CalendarToday"),
  }),
  page("admin.promotions", () => import("../pages/AdminPromotions"), {
    title: "Promotions",
    guard: isAdmin,
    nav: marketingNav("Promotions", "AllInboxIcon"),
  }),
  page("admin.promo-code-manager", () => import("../pages/PromoCodeManager"), {
    guard: isAdmin,
    nav: marketingNav("Promo Code Manager", "ReceiptIcon"),
  }),
  page("admin.reports", () => import("../pages/Reports"), {
    title: "Reports",
    guard: seesReports,
    nav: marketingNav("Reports", "ListOutlined"),
  }),
  page("admin.waitlists", () => import("../pages/Waitlists"), {
    guard: isAdmin,
    nav: marketingNav("Waitlists", "QueueIcon"),
  }),
  page("admin.query", () => import("../pages/AdminQuery"), {
    title: "Admin Query",
    guard: isAdmin,
    nav: systemNav("Admin Query", "ViewComfy"),
  }),
  page(
    "admin.site-configuration",
    () => import("../pages/AdminSiteConfiguration"),
    {
      title: "Site Configuration",
      guard: isAdmin,
      nav: systemNav("Site Configuration", "SettingsOutlined"),
    },
  ),
  page("admin.tag-approval", () => import("../pages/AdminTagApproval"), {
    title: "Tag Approval",
    guard: isAdmin,
    nav: systemNav("Tag Approval", "MoreOutlined"),
  }),
  page("admin.tag-manager", () => import("../pages/AdminTagManager"), {
    title: "Tag Manager",
    guard: isAdmin,
    nav: systemNav("Tag Manager", "MoreOutlined"),
  }),
  page("admin.analytics", () => import("../pages/AdminAnalyticsDashboard"), {
    title: "Analytics",
    guard: isSuperadmin,
    nav: systemNav("Analytics", "InsertChart"),
  }),
  page("admin.autopay-list", () => import("../pages/AdminAutoPayReport"), {
    title: "Autopay Shops",
    guard: isSuperadmin,
    nav: systemNav("Autopay Shops", "ShopOutlined"),
  }),
  page("admin.verticals", () => import("../pages/AdminVerticals"), {
    title: "Verticals",
    guard: isSuperadmin,
    nav: systemNav("Verticals", "StyleIcon"),
  }),
  page("admin.upload-orders", () => import("../pages/AdminUploadOrders"), {
    title: "Upload Orders",
    guard: isSuperadmin,
    nav: systemNav("Upload Orders", "PublishIcon"),
  }),
];

export const PAGES = Object.freeze([
  ...SHOP,
  ...DASHBOARD,
  ...ACCOUNT,
  ...ADMIN,
]);
